<template>
    <div class="user-info-container user-info-page nav-content-inset">
      <el-card class="user-info-card">
        <template #header>
          <div class="card-header user-settings-header">
            <h2>个人朋友信息</h2>
            <el-button type="primary" @click="handleEdit" v-if="!isEditing">
              <el-icon><Edit /></el-icon>
              编辑信息
            </el-button>
            <div class="edit-buttons" v-else>
              <el-button type="success" @click="handleSave">
                <el-icon><Check /></el-icon>
                保存
              </el-button>
              <el-button @click="handleCancel">
                <el-icon><Close /></el-icon>
                取消
              </el-button>
            </div>
          </div>
        </template>

        <div class="friend-feature-bar">
          <div class="friend-feature-row">
            <span class="friend-feature-label">朋友功能</span>
            <el-switch
              v-model="friendEnabled"
              :loading="friendFeatureLoading"
              :disabled="friendFeatureLoading"
              @change="handleFriendFeatureChange"
            />
          </div>
          <p class="field-hint">
            开启后可被其他用户搜索，并查看其他已开启用户。需至少 3 张个人照片（当前 {{ personalPhotoCount }} 张）。
          </p>
        </div>

        <VisibilitySettingsPanel />

        <div class="profile-summary">
          <img v-if="userForm.avatar" :src="userForm.avatar" class="profile-summary__avatar" alt="" />
          <div class="profile-summary__body">
            <div class="profile-summary__name">{{ userForm.nickname || '未设置昵称' }}</div>
            <div class="profile-summary__meta">
              <span>{{ genderLabel }}</span>
              <span v-if="ageLabel">{{ ageLabel }}岁</span>
              <span>{{ livePlace || '现居未填' }}</span>
              <span v-if="userForm.occupation">{{ userForm.occupation }}</span>
              <span v-if="userForm.education">{{ userForm.education }}</span>
            </div>
            <div class="profile-summary__tags" v-if="displayTags.length">
              <el-tag v-for="t in displayTags" :key="t" size="small" effect="plain">{{ t }}</el-tag>
            </div>
          </div>
        </div>
        
        <el-form
          :model="userForm"
          :rules="rules"
          ref="userForm"
          class="user-settings-form"
          :label-position="compact ? 'top' : 'right'"
          :label-width="compact ? 'auto' : '100px'"
        >
          <h3 class="profile-section-title">基本资料</h3>
          <el-row :gutter="compact ? 0 : 20">
            <el-col :xs="24" :sm="12" class="avatar-section">
              <el-form-item label="头像">
                <el-upload
                  class="avatar-uploader"
                  action="/api/v1/upload"
                  :show-file-list="false"
                  :on-success="handleAvatarSuccess"
                  :before-upload="beforeAvatarUpload"
                  :disabled="!isEditing"
                  :headers="authHeaders"
                >
                  <div class="avatar-container">
                    <img v-if="userForm.avatar" :src="userForm.avatar" class="avatar">
                    <el-icon v-else class="avatar-uploader-icon"><Plus /></el-icon>
                    <div v-if="userForm.avatar && userForm.avatar.includes('tobeaudit')" class="avatar-audit-overlay">
                      <span class="audit-text">待审核</span>
                    </div>
                  </div>
                </el-upload>
              </el-form-item>
            </el-col>
            <el-col :xs="24" :sm="12">
              <el-form-item label="昵称（展示名）" prop="nickname">
                <template v-if="canChangeNickname">
                  <div class="username-auto-hint">
                    <el-tag type="warning" size="small" effect="light">可改一次</el-tag>
                    <span class="username-current">{{ userForm.nickname || '—' }}</span>
                  </div>
                  <el-input
                    v-model="newNickname"
                    placeholder="设置你的昵称"
                    maxlength="20"
                    clearable
                  />
                  <p class="field-hint">昵称用于朋友/动态等展示，不是登录名。微信扫码昵称可自定义一次（2-20 字）。</p>
                  <el-button
                    type="primary"
                    :loading="nicknameSaving"
                    :disabled="!newNickname.trim()"
                    @click="handleChangeNickname"
                  >
                    保存昵称
                  </el-button>
                </template>
                <template v-else>
                  <el-input v-model="userForm.nickname" :disabled="!isEditing"></el-input>
                  <p class="field-hint">昵称用于展示；登录名请到「账号安全」管理。</p>
                </template>
              </el-form-item>
            </el-col>
          </el-row>

          <el-row :gutter="compact ? 0 : 20">
            <el-col :xs="24" :sm="24">
              <el-form-item label="登录名">
                <div class="username-field">
                  <div class="username-display">
                    <span>{{ accountUsername || '—' }}</span>
                    <el-tag v-if="usernameAuto" type="warning" size="small" effect="light">待设置</el-tag>
                  </div>
                  <p class="field-hint">
                    登录名用于账号密码登录；昵称仅用于展示，二者不同。
                    修改登录名 / 设置密码 / 绑定邮箱请到
                    <router-link to="/user/password">账号安全</router-link>。
                  </p>
                </div>
              </el-form-item>
            </el-col>
          </el-row>
  
          <el-row :gutter="20">
            <el-col :xs="24" :sm="12">
              <el-form-item label="性别" prop="gender">
                <el-select v-model="userForm.gender" :disabled="!isEditing">
                  <el-option label="男" value="male"></el-option>
                  <el-option label="女" value="female"></el-option>
                
                </el-select>
              </el-form-item>
            </el-col>
            <el-col :xs="24" :sm="12">
              <el-form-item label="生日" prop="birthday">
                <el-date-picker
                  v-model="userForm.birthday"
                  type="date"
                  placeholder="选择日期"
                  :disabled="!isEditing"
                ></el-date-picker>
              </el-form-item>
            </el-col>
          </el-row>
  
          <el-row :gutter="20">
            <el-col :xs="24" :sm="12">
              <el-form-item label="身高(cm)" prop="height">
                <el-input-number v-model="userForm.height" :min="0" :max="300" :disabled="!isEditing"></el-input-number>
              </el-form-item>
            </el-col>
            <el-col :xs="24" :sm="12">
              <el-form-item label="体重(kg)" prop="weight">
                <el-input-number v-model="userForm.weight" :min="0" :max="500" :disabled="!isEditing"></el-input-number>
              </el-form-item>
            </el-col>
          </el-row>
  
          <h3 class="profile-section-title">现居</h3>
          <el-row :gutter="20">
            <el-col :xs="24" :sm="12">
              <el-form-item label="国家" prop="country">
                <el-input v-model="userForm.country" :disabled="!isEditing"></el-input>
              </el-form-item>
            </el-col>
            <el-col :xs="24" :sm="12">
              <el-form-item label="省份" prop="province">
                <el-cascader
                  v-model="userForm.pcaValue"
                  :options="pcaOptions"
                  :props="{ expandTrigger: 'hover' }"
                  :disabled="!isEditing"
                  placeholder="请选择省/市/区"
                  @change="handlePcaChange"
                ></el-cascader>
              </el-form-item>
            </el-col>
          </el-row>
  
          <el-row :gutter="20">
            <el-col :xs="24" :sm="12">
              <el-form-item label="城市" prop="city">
                <div class="place-row">
                  <el-input v-model="userForm.city" :disabled="!isEditing"></el-input>
                  <el-button :disabled="!isEditing" @click="openMapPicker('current')">在地图上选</el-button>
                </div>
              </el-form-item>
            </el-col>
            <el-col :xs="24" :sm="12">
              <el-form-item label="区县" prop="district">
                <el-input v-model="userForm.district" :disabled="!isEditing"></el-input>
              </el-form-item>
            </el-col>
          </el-row>
          <el-row :gutter="20">
            <el-col :span="24">
              <el-form-item label="住址">
                <el-input v-model="userForm.location" :disabled="!isEditing" placeholder="详细住址，可在地图上点选"></el-input>
              </el-form-item>
            </el-col>
          </el-row>
  
          <h3 class="profile-section-title">家乡</h3>
          <el-row :gutter="20">
            <el-col :xs="24" :sm="12">
              <el-form-item label="家乡国家" prop="homeCountry">
                <el-input v-model="userForm.homeCountry" :disabled="!isEditing"></el-input>
              </el-form-item>
            </el-col>
            <el-col :xs="24" :sm="12">
              <el-form-item label="家乡省份" prop="homeProvince">
                <el-cascader
                  v-model="userForm.homePcaValue"
                  :options="homePcaOptions"
                  :props="{ expandTrigger: 'hover' }"
                  :disabled="!isEditing"
                  placeholder="请选择省/市/区"
                  @change="handleHomePcaChange"
                ></el-cascader>
              </el-form-item>
            </el-col>
          </el-row>
  
          <el-row :gutter="20">
            <el-col :xs="24" :sm="12">
              <el-form-item label="家乡城市" prop="homeCity">
                <div class="place-row">
                  <el-input v-model="userForm.homeCity" :disabled="!isEditing"></el-input>
                  <el-button :disabled="!isEditing" @click="openMapPicker('home')">在地图上选</el-button>
                </div>
              </el-form-item>
            </el-col>
            <el-col :xs="24" :sm="12">
              <el-form-item label="家乡区县" prop="homeDistrict">
                <el-input v-model="userForm.homeDistrict" :disabled="!isEditing"></el-input>
              </el-form-item>
            </el-col>
          </el-row>
  
          <h3 class="profile-section-title">学业与职业</h3>
          <el-row :gutter="20">
            <el-col :xs="24" :sm="12">
              <el-form-item label="学历" prop="education">
                <el-select v-model="userForm.education" :disabled="!isEditing">
                  <el-option label="高中" value="高中"></el-option>
                  <el-option label="大专" value="大专"></el-option>
                  <el-option label="本科" value="本科"></el-option>
                  <el-option label="硕士" value="硕士"></el-option>
                  <el-option label="博士" value="博士"></el-option>
                </el-select>
              </el-form-item>
            </el-col>
            <el-col :xs="24" :sm="12">
              <el-form-item label="学校">
                <el-input v-model="userForm.school" :disabled="!isEditing" placeholder="毕业或在读学校"></el-input>
              </el-form-item>
            </el-col>
          </el-row>
          <el-row :gutter="20">
            <el-col :xs="24" :sm="12">
              <el-form-item label="职业" prop="occupation">
                <el-input v-model="userForm.occupation" :disabled="!isEditing"></el-input>
              </el-form-item>
            </el-col>
            <el-col :xs="24" :sm="12">
              <el-form-item label="工作单位">
                <el-input v-model="userForm.workplace" :disabled="!isEditing" placeholder="公司或机构"></el-input>
              </el-form-item>
            </el-col>
          </el-row>
  
          <h3 class="profile-section-title">生活</h3>
          <el-row :gutter="20">
            <el-col :xs="24" :sm="12">
              <el-form-item label="收入范围" prop="income">
                <el-select v-model="userForm.income" :disabled="!isEditing">
                  <el-option label="无收入" value="0"></el-option>
                  <el-option label="5k以下" value="5"></el-option>
                  <el-option label="5k-10k" value="10"></el-option>
                  <el-option label="10k-20k" value="20"></el-option>
                  <el-option label="20k-30k" value="30"></el-option>
                  <el-option label="30k-50k" value="50"></el-option>
                  <el-option label="50k-100k" value="100"></el-option>
                  <el-option label="100k-200k" value="200"></el-option>
                  <el-option label="200k-500k" value="500"></el-option>
                  <el-option label="500k以上" value="1000"></el-option>
                </el-select>
              </el-form-item>
            </el-col>
            <el-col :xs="24" :sm="12">
              <el-form-item label="婚姻状况" prop="maritalStatus">
                <el-select v-model="userForm.maritalStatus" :disabled="!isEditing">
                  <el-option label="未婚" value="未婚"></el-option>
                  <el-option label="离异" value="离异"></el-option>
                  <el-option label="丧偶" value="丧偶"></el-option>
                </el-select>
              </el-form-item>
            </el-col>
          </el-row>
  
          <el-row :gutter="20">
            <el-col :xs="24" :sm="12">
              <el-form-item label="男孩数量" prop="boyCount">
                <el-input-number v-model="userForm.boyCount" :min="0" :max="10" :disabled="!isEditing"></el-input-number>
              </el-form-item>
            </el-col>
            <el-col :xs="24" :sm="12">
              <el-form-item label="女孩数量" prop="girlCount">
                <el-input-number v-model="userForm.girlCount" :min="0" :max="10" :disabled="!isEditing"></el-input-number>
              </el-form-item>
            </el-col>
          </el-row>
  
          <el-row :gutter="20">
            <el-col :xs="24" :sm="12">
              <el-form-item label="宗教信仰" prop="religion">
                <el-select v-model="userForm.religion" :disabled="!isEditing" placeholder="请选择宗教信仰">
                  <el-option label="无宗教信仰" value="无宗教信仰"></el-option>
                  <el-option label="基督教" value="基督教"></el-option>
                  <el-option label="伊斯兰教" value="伊斯兰教"></el-option>
                  <el-option label="印度教" value="印度教"></el-option>
                  <el-option label="佛教" value="佛教"></el-option>
                  <el-option label="犹太教" value="犹太教"></el-option>
                  <el-option label="锡克教" value="锡克教"></el-option>
                  <el-option label="道教" value="道教"></el-option>
                  <el-option label="儒家思想" value="儒家思想"></el-option>
                  <el-option label="耆那教" value="耆那教"></el-option>
                  <el-option label="神道教" value="神道教"></el-option>
                  <el-option label="巴哈伊教" value="巴哈伊教"></el-option>
                  <el-option label="琐罗亚斯德教" value="琐罗亚斯德教"></el-option>
                  <el-option label="民间信仰" value="民间信仰"></el-option>
                </el-select>
              </el-form-item>
            </el-col>
          </el-row>
  
          <el-form-item label="兴趣爱好" prop="hobbies">
            <el-select
              v-model="userForm.hobbies"
              multiple
              filterable
              :disabled="!isEditing"
              placeholder="请选择兴趣爱好"
            >
              <el-option-group
                v-for="category in interestCategories"
                :key="category.code"
                :label="category.category"
              >
                <el-option-group
                  v-for="subcategory in category.subcategories"
                  :key="subcategory.code"
                  :label="subcategory.subcategory"
                >
                  <el-option
                    v-for="item in subcategory.items"
                    :key="item.code"
                    :label="item.name"
                    :value="item.code"
                  ></el-option>
                </el-option-group>
              </el-option-group>
            </el-select>
          </el-form-item>
  
          <el-form-item label="标签" prop="tags">
            <el-select
              v-model="userForm.tags"
              multiple
              filterable
              :disabled="!isEditing"
              placeholder="个性 + 单身分类，仅可从字典选择"
            >
              <el-option-group
                v-for="category in tagGroups"
                :key="category.group"
                :label="category.label"
              >
                <el-option
                  v-for="tag in category.tags"
                  :key="tag.code"
                  :label="tag.name"
                  :value="tag.name"
                ></el-option>
              </el-option-group>
            </el-select>
          </el-form-item>
  
          <h3 class="profile-section-title">关于我</h3>
          <el-form-item label="个人简介" prop="introduction">
            <el-input
              type="textarea"
              v-model="userForm.introduction"
              :rows="4"
              :disabled="!isEditing"
              placeholder="介绍一下你自己：工作、籍贯、住址、爱好、学历、学校、职业等，可用 AI 梳理"
            ></el-input>
            <el-button class="ai-btn" type="primary" plain :loading="aiAnalyzing" :disabled="!isEditing" @click="analyzeProfile">
              AI 梳理
            </el-button>
          </el-form-item>
  
          <h3 class="profile-section-title">相册</h3>
          <el-form-item label="相册照片" prop="photos">
            <el-upload
              action="/api/v1/upload"
              list-type="picture-card"
              :on-success="handlePhotoSuccess"
              :before-upload="beforePhotoUpload"
              :disabled="!isEditing"
              multiple
              :headers="authHeaders"
              :file-list="photoFileList"
              :on-remove="handlePhotoRemove"
            >
              <el-icon><Plus /></el-icon>
              <template #file="{ file }">
                <div class="photo-item-container">
                  <img :src="file.url" class="photo-item" />
                  <div v-if="file.url && file.url.includes('tobeaudit')" class="photo-audit-overlay">
                    <span class="audit-text">待审核</span>
                  </div>
                  <div class="photo-actions">
                    <el-button
                      type="primary"
                      circle
                      size="small"
                      @click.stop="moveToFirst(file)"
                      v-if="isEditing"
                    >
                      <el-icon><Top /></el-icon>
                    </el-button>
                    <el-button
                      type="danger"
                      circle
                      size="small"
                      @click.stop="handlePhotoRemove(file)"
                      v-if="isEditing"
                    >
                      <el-icon><Delete /></el-icon>
                    </el-button>
                  </div>
                </div>
              </template>
            </el-upload>
          </el-form-item>
        </el-form>
      </el-card>

      <el-dialog
        v-model="mapDialog.visible"
        :title="mapDialog.target === 'home' ? '选择家乡位置' : '选择现居位置'"
        width="720px"
        destroy-on-close
        @closed="mapDialog.target = ''"
      >
        <p class="field-hint">在地图上点击或搜索地点，选定后回填城市和住址。</p>
        <city-picker
          v-if="mapDialog.visible"
          :map-id="mapDialog.target === 'home' ? 'home-location-map' : 'current-location-map'"
          :initial-city="mapDialog.target === 'home' ? userForm.homeCity : userForm.city"
          @select="onMapPicked"
          @map-click="handleMapClick"
        />
      </el-dialog>
  
      <el-card class="security-card">
        <template #header>
          <div class="card-header user-settings-header">
            <h2>安全设置</h2>
          </div>
        </template>
        <div class="security-actions">
          <el-button type="primary" @click="goChangePassword">修改密码</el-button>
        </div>
      </el-card>
    </div>
  </template>
  
  <script>
  import { Edit, Check, Close, Plus, Delete, Top } from '@element-plus/icons-vue'
  import { validatePhotoUpload, photoPathToUrl, photoUrlToStoragePath, resolveProfilePhotoUrl } from '@/utils/photoUpload'
  import CityPicker from '@/components/CityPicker.vue'
  import VisibilitySettingsPanel from '@/components/VisibilitySettingsPanel.vue'
  import pcaData from './pca-code.json'
  import interestData from './interest.json'
  import personalityData from './Personality.json'
  import { isCompactViewport } from '@/utils/browser'
  import {
    WECHAT_USERNAME_HINT,
    validateWechatUsernameChange,
    isAutoGeneratedUsername,
    canChangeUsername as canChangeUsernameUtil
  } from '@/utils/username'
  
  export default {
    name: 'UserInfoView',
    components: {
      Edit,
      Check,
      Close,
      Plus,
      Delete,
      CityPicker,
      VisibilitySettingsPanel,
      Top
    },
    data() {
      const validatePass = (rule, value, callback) => {
        if (value === '') {
          callback(new Error('请输入密码'))
        } else {
          if (this.securityForm.confirmPassword !== '') {
            this.$refs.securityForm.validateField('confirmPassword')
          }
          callback()
        }
      }
      const validatePass2 = (rule, value, callback) => {
        if (value === '') {
          callback(new Error('请再次输入密码'))
        } else if (value !== this.securityForm.newPassword) {
          callback(new Error('两次输入密码不一致!'))
        } else {
          callback()
        }
      }
      
      return {
        compact: isCompactViewport(),
        accountUsername: '',
        usernameAuto: false,
        newUsername: '',
        usernameSaving: false,
        nicknameCustomizable: false,
        newNickname: '',
        nicknameSaving: false,
        wechatUsernameHint: WECHAT_USERNAME_HINT,
        isEditing: false,
        userInfo: {
          username: '',
          nickname: '',
          sysRole: ''
        },
        userForm: {
          nickname: '',
          avatar: '',
          gender: '',
          birthday: '',
          height: 0,
          weight: 0,
          country: '',
          province: '',
          city: '',
          district: '',
          pcaValue: [],
          homeCountry: '',
          homeProvince: '',
          homeCity: '',
          homeDistrict: '',
          homePcaValue: [],
          location: '',
          education: '',
          school: '',
          occupation: '',
          workplace: '',
          income: '',
          maritalStatus: '',
          boyCount: 0,
          girlCount: 0,
          religion: '',
          hobbies: [],
          tags: [],
          introduction: '',
          photos: [],
          lat: null,
          lng: null,
          homeLat: null,
          homeLng: null,
          isVerified: 0,
          vipLevel: 0
        },
        pcaOptions: [],
        homePcaOptions: [],
        securityForm: {
          currentPassword: '',
          newPassword: '',
          confirmPassword: ''
        },
        rules: {
          nickname: [
            { required: true, message: '请输入昵称', trigger: 'blur' }
          ],
          height: [
            { type: 'number', message: '请输入有效的身高', trigger: 'blur' }
          ],
          weight: [
            { type: 'number', message: '请输入有效的体重', trigger: 'blur' }
          ]
        },
        securityRules: {
          currentPassword: [
            { required: true, message: '请输入当前密码', trigger: 'blur' }
          ],
          newPassword: [
            { required: true, message: '请输入新密码', trigger: 'blur' },
            { validator: validatePass, trigger: 'blur' }
          ],
          confirmPassword: [
            { required: true, message: '请再次输入新密码', trigger: 'blur' },
            { validator: validatePass2, trigger: 'blur' }
          ]
        },
        hobbyOptions: [],
        tagOptions: [],
        photoFileList: [],
        friendEnabled: false,
        friendFeatureLoading: false,
        interestCategories: [],
        personalityCategories: [],
        tagDict: [],
        aiAnalyzing: false,
        mapDialog: { visible: false, target: 'current' },
      }
    },
    computed: {
      authHeaders() {
        return {
          'Authorization': `Bearer ${localStorage.getItem('jwt_token')}`
        }
      },
      canChangeUsername() {
        return canChangeUsernameUtil(this.accountUsername, this.usernameAuto ? 0 : 1)
      },
      canChangeNickname() {
        return this.nicknameCustomizable
      },
      personalPhotoCount() {
        const list = this.photoFileList.length ? this.photoFileList : (this.userForm.photos || [])
        return list.filter(item => {
          const url = typeof item === 'string' ? item : item?.url
          return url && String(url).trim() !== ''
        }).length
      },
      genderLabel() {
        return ({ male: '男', female: '女' })[this.userForm.gender] || '未填性别'
      },
      ageLabel() {
        const b = this.userForm.birthday
        if (!b) return ''
        const d = b instanceof Date ? b : new Date(b)
        if (Number.isNaN(d.getTime())) return ''
        const age = Math.floor((Date.now() - d.getTime()) / (365.25 * 24 * 3600 * 1000))
        return age > 0 && age < 120 ? String(age) : ''
      },
      livePlace() {
        return [this.userForm.province, this.userForm.city, this.userForm.district].filter(Boolean).join(' ')
      },
      tagGroups() {
        const personality = this.tagDict.filter(t => t.tagGroup === 'personality')
        const klass = this.tagDict.filter(t => t.tagGroup === 'class')
        const groups = []
        if (klass.length) groups.push({ group: 'class', label: '单身分类', tags: klass })
        if (personality.length) groups.push({ group: 'personality', label: '个性', tags: personality })
        if (!groups.length && this.personalityCategories.length) {
          const tags = []
          this.personalityCategories.forEach(c => (c.tags || []).forEach(t => tags.push({ code: t.code, name: t.name })))
          groups.push({ group: 'personality', label: '个性', tags })
        }
        return groups
      },
      displayTags() {
        return (this.userForm.tags || []).map(t => this.tagDisplayName(t)).filter(Boolean)
      },
      flattenedInterests() {
        const interests = []
        this.interestCategories.forEach(category => {
          category.subcategories.forEach(subcategory => {
            subcategory.items.forEach(item => {
              interests.push({
                label: `${category.category} - ${subcategory.subcategory} - ${item.name}`,
                value: item.code,
                category: category.category,
                subcategory: subcategory.subcategory
              })
            })
          })
        })
        return interests
      }
    },
    created() {
      this.initPcaData()
      this.initInterestData()
      this.initPersonalityData()
      this.fetchTagDict()
      this.fetchUserProfile()
      this.fetchAccountInfo()
    },
    mounted() {
      this.onResize = () => {
        this.compact = isCompactViewport()
      }
      window.addEventListener('resize', this.onResize)
    },
    beforeUnmount() {
      if (this.onResize) {
        window.removeEventListener('resize', this.onResize)
      }
    },
    methods: {
      initPcaData() {
        this.pcaOptions = pcaData.map(province => ({
          value: province.code,
          label: province.name,
          children: province.children.map(city => ({
            value: city.code,
            label: city.name,
            children: city.children.map(district => ({
              value: district.code,
              label: district.name
            }))
          }))
        }))
        this.homePcaOptions = JSON.parse(JSON.stringify(this.pcaOptions))
      },
      initInterestData() {
        this.interestCategories = interestData
      },
      initPersonalityData() {
        this.personalityCategories = personalityData
      },
      tagDisplayName(raw) {
        const v = String(raw || '').trim()
        if (!v) return ''
        const hit = this.tagDict.find(t => t.name === v || t.code === v)
        if (hit) return hit.name
        for (const cat of this.personalityCategories) {
          const t = (cat.tags || []).find(x => x.code === v || x.name === v)
          if (t) return t.name
        }
        return v
      },
      async fetchTagDict() {
        try {
          const token = localStorage.getItem('jwt_token')
          const res = await fetch('/api/v1/user/profile/tag-dict', {
            method: 'POST',
            headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
            body: '{}'
          })
          const data = await res.json()
          if (data.code === 200) {
            this.tagDict = data.data?.tags || []
          }
        } catch (e) {
          console.error('load tag dict', e)
        }
      },
      openMapPicker(target) {
        this.mapDialog.target = target
        this.mapDialog.visible = true
      },
      onMapPicked(obj) {
        if (this.mapDialog.target === 'home') {
          this.handleHomeCitySelect(obj)
        } else {
          this.handleCitySelect(obj)
        }
        this.mapDialog.visible = false
      },
      async analyzeProfile() {
        const intro = (this.userForm.introduction || '').trim()
        if (!intro) {
          this.$message.warning('请先填写个人简介')
          return
        }
        this.aiAnalyzing = true
        try {
          const token = localStorage.getItem('jwt_token')
          const res = await fetch('/api/v1/user/profile/ai-analyze', {
            method: 'POST',
            headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
            body: JSON.stringify({
              introduction: intro,
              occupation: this.userForm.occupation,
              education: this.userForm.education,
              school: this.userForm.school,
              workplace: this.userForm.workplace,
              location: this.userForm.location,
              homeCity: this.userForm.homeCity,
              hobbies: this.userForm.hobbies,
              tags: this.userForm.tags
            })
          })
          const data = await res.json()
          if (data.code !== 200 || !data.data) {
            this.$message.error(data.message || 'AI 梳理失败')
            return
          }
          const d = data.data
          const fill = (key, val) => {
            if (val !== undefined && val !== null && String(val).trim() !== '') {
              this.userForm[key] = val
            }
          }
          fill('occupation', d.occupation)
          fill('workplace', d.workplace)
          fill('school', d.school)
          fill('education', d.education)
          fill('homeCountry', d.homeCountry)
          fill('homeProvince', d.homeProvince)
          fill('homeCity', d.homeCity)
          fill('homeDistrict', d.homeDistrict)
          fill('location', d.location)
          if (Array.isArray(d.hobbies) && d.hobbies.length) {
            const mapped = d.hobbies.map(n => this.hobbyCodeFromName(n)).filter(Boolean)
            this.userForm.hobbies = Array.from(new Set([...(this.userForm.hobbies || []), ...mapped]))
          }
          if (Array.isArray(d.tags) && d.tags.length) {
            const mapped = d.tags.map(t => this.tagDisplayName(t)).filter(Boolean)
            this.userForm.tags = Array.from(new Set([...(this.userForm.tags || []).map(t => this.tagDisplayName(t)), ...mapped]))
          }
          this.setPcaValue()
          this.$message.success(data.message || '已填入表单，请核对后保存')
        } catch (e) {
          this.$message.error(e.message || 'AI 梳理失败')
        } finally {
          this.aiAnalyzing = false
        }
      },
      hobbyCodeFromName(name) {
        const n = String(name || '').trim()
        if (!n) return ''
        let found = ''
        this.interestCategories.forEach(category => {
          (category.subcategories || []).forEach(sub => {
            (sub.items || []).forEach(item => {
              if (item.code === n || item.name === n) found = item.code
            })
          })
        })
        return found
      },
      setPcaValue() {
        if (this.userForm.province && this.userForm.city && this.userForm.district) {
          const province = this.pcaOptions.find(p => p.label === this.userForm.province)
          if (province) {
            const city = province.children.find(c => c.label === this.userForm.city)
            if (city) {
              const district = city.children.find(d => d.label === this.userForm.district)
              if (district) {
                this.userForm.pcaValue = [province.value, city.value, district.value]
              }
            }
          }
        }

        if (this.userForm.homeProvince && this.userForm.homeCity && this.userForm.homeDistrict) {
          const province = this.homePcaOptions.find(p => p.label === this.userForm.homeProvince)
          if (province) {
            const city = province.children.find(c => c.label === this.userForm.homeCity)
            if (city) {
              const district = city.children.find(d => d.label === this.userForm.homeDistrict)
              if (district) {
                this.userForm.homePcaValue = [province.value, city.value, district.value]
              }
            }
          }
        }
      },
      handlePcaChange(value) {
        if (value && value.length > 0) {
          const [provinceCode, cityCode, districtCode] = value
          const province = this.pcaOptions.find(p => p.value === provinceCode)
          if (province) {
            this.userForm.province = province.label
            const city = province.children.find(c => c.value === cityCode)
            if (city) {
              this.userForm.city = city.label
              const district = city.children.find(d => d.value === districtCode)
              if (district) {
                this.userForm.district = district.label
              }
            }
          }
        }
      },
      handleHomePcaChange(value) {
        if (value && value.length > 0) {
          const [provinceCode, cityCode, districtCode] = value
          const province = this.homePcaOptions.find(p => p.value === provinceCode)
          if (province) {
            this.userForm.homeProvince = province.label
            const city = province.children.find(c => c.value === cityCode)
            if (city) {
              this.userForm.homeCity = city.label
              const district = city.children.find(d => d.value === districtCode)
              if (district) {
                this.userForm.homeDistrict = district.label
              }
            }
          }
        }
      },
      async fetchAccountInfo() {
        try {
          const token = localStorage.getItem('jwt_token')
          const response = await fetch('/api/v1/user/info', {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: '{}'
          })
          const data = await response.json()
          if (data.code === 200 && data.data) {
            this.accountUsername = data.data.username || ''
            this.usernameAuto = !!data.data.usernameAuto
            this.nicknameCustomizable = !!data.data.nicknameCustomizable
            if (!this.usernameAuto) {
              this.usernameAuto = isAutoGeneratedUsername(this.accountUsername)
            }
            const stored = JSON.parse(localStorage.getItem('user_info') || '{}')
            stored.username = this.accountUsername
            stored.usernameAuto = this.usernameAuto
            localStorage.setItem('user_info', JSON.stringify(stored))
            if (this.$root?.userInfo) {
              this.$root.userInfo.username = this.accountUsername
              this.$root.userInfo.usernameAuto = this.usernameAuto
            }
          }
        } catch (error) {
          console.error('获取账号信息失败:', error)
        }
      },
      async handleChangeUsername() {
        const name = (this.newUsername || '').trim()
        const check = validateWechatUsernameChange(name)
        if (!check.ok) {
          this.$message.error(check.message || this.wechatUsernameHint)
          return
        }
        if (name === this.accountUsername) {
          this.$message.warning('新用户名不能与当前用户名相同')
          return
        }
        try {
          this.usernameSaving = true
          const token = localStorage.getItem('jwt_token')
          const response = await fetch('/api/v1/user/username', {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({ username: name })
          })
          const data = await response.json()
          if (data.code === 200) {
            this.accountUsername = data.data?.username || name
            this.usernameAuto = false
            this.newUsername = ''
            this.$message.success('用户名已更新')
            const stored = JSON.parse(localStorage.getItem('user_info') || '{}')
            stored.username = this.accountUsername
            stored.usernameAuto = false
            localStorage.setItem('user_info', JSON.stringify(stored))
            if (this.$root?.userInfo) {
              this.$root.userInfo.username = this.accountUsername
              this.$root.userInfo.usernameAuto = false
            }
          } else {
            this.$message.error(data.message || '用户名更新失败')
          }
        } catch (error) {
          this.$message.error('用户名更新失败')
        } finally {
          this.usernameSaving = false
        }
      },
      async handleChangeNickname() {
        const name = (this.newNickname || '').trim()
        if (name.length < 2 || name.length > 20) {
          this.$message.error('昵称需为 2-20 个字')
          return
        }
        if (name === this.userForm.nickname) {
          this.$message.warning('新昵称不能与当前昵称相同')
          return
        }
        try {
          this.nicknameSaving = true
          const token = localStorage.getItem('jwt_token')
          const response = await fetch('/api/v1/user/nickname', {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({ nickname: name })
          })
          const data = await response.json()
          if (data.code === 200) {
            this.userForm.nickname = name
            this.nicknameCustomizable = false
            this.newNickname = ''
            this.$message.success('昵称已更新')
            const stored = JSON.parse(localStorage.getItem('user_info') || '{}')
            stored.nickname = name
            localStorage.setItem('user_info', JSON.stringify(stored))
            if (this.$root?.userInfo) {
              this.$root.userInfo.nickname = name
            }
          } else {
            this.$message.error(data.message || '昵称更新失败')
          }
        } catch (error) {
          this.$message.error('昵称更新失败')
        } finally {
          this.nicknameSaving = false
        }
      },
      async fetchUserProfile() {
        try {
          const token = localStorage.getItem('jwt_token')
          const uuid = localStorage.getItem('uuid')
          const response = await fetch('/api/v1/user/myprofile/get', {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              
              uuid: uuid
            })
          })

          const data = await response.json()
          if (data.code === 200 && data.data) {
            const birthday = data.data.birthday ? new Date(
              Math.floor(data.data.birthday / 10000),
              Math.floor((data.data.birthday % 10000) / 100) - 1,
              data.data.birthday % 100
            ) : null

            //如果 data.data.avatar 包含 tobeaudit , 就在对应的图片上方加个蒙层和 "待审核" 字样
             

            const photos = (data.data.photos || [])
              .filter(photo => photo && photo.trim() !== '')
              .map(photo => photoPathToUrl(photo))
            this.photoFileList = photos.map(photo => ({
              name: photo.split('/').pop(),
              url: photo,
              status: 'success',
              uid: Date.now() + Math.random()
            }))

            this.userForm = {
              nickname: data.data.nickname,
              avatar: resolveProfilePhotoUrl(data.data.avatar, data.data.photo),
              gender: data.data.sex,
              birthday: birthday,
              height: data.data.height,
              weight: data.data.weight,
              country: data.data.country,
              province: data.data.province,
              city: data.data.city,
              district: data.data.district,
              pcaValue: [],
              homeCountry: data.data.homeCountry,
              homeProvince: data.data.homeProvince,
              homeCity: data.data.homeCity,
              homeDistrict: data.data.homeDistrict,
              homePcaValue: [],
              location: data.data.location,
              education: data.data.education,
              school: data.data.school || '',
              occupation: data.data.occupation,
              workplace: data.data.workplace || '',
              income: data.data.income,
              maritalStatus: data.data.maritalStatus,
              boyCount: data.data.boyCount,
              girlCount: data.data.girlCount,
              religion: data.data.religion,
              hobbies: data.data.hobbies || [],
              tags: (data.data.tags || []).map(t => this.tagDisplayName(t)).filter(Boolean),
              introduction: data.data.introduction,
              photos: photos,
              lat: data.data.lat ? data.data.lat / 10000 : null,
              lng: data.data.lng ? data.data.lng / 10000 : null,
              homeLat: data.data.homeLat ? data.data.homeLat / 10000 : null,
              homeLng: data.data.homeLng ? data.data.homeLng / 10000 : null,
              isVerified: data.data.isVerified,
              vipLevel: data.data.vipLevel
            }

            this.$nextTick(() => {
              this.setPcaValue()
            })
            if (data.data.username) {
              this.accountUsername = data.data.username
            }
            if (data.data.usernameAuto !== undefined) {
              this.usernameAuto = !!data.data.usernameAuto
            }
            this.friendEnabled = !!data.data.friendEnabled
          } else {
            this.$message.error(data.message || '获取用户信息失败')
          }
        } catch (error) {
          console.error('获取用户信息失败:', error)
          this.$message.error('获取用户信息失败')
        }
      },
      handleEdit() {
        this.isEditing = true
      },
      async handleFriendFeatureChange(enabled) {
        if (enabled && this.personalPhotoCount < 3) {
          this.friendEnabled = false
          this.$message.warning('开启朋友功能需要至少3张个人照片，请先上传并保存')
          return
        }
        const prev = !enabled
        this.friendFeatureLoading = true
        try {
          const token = localStorage.getItem('jwt_token')
          const response = await fetch('/api/v1/user/friend/feature', {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({ enabled })
          })
          const data = await response.json()
          if (data.code === 200) {
            this.friendEnabled = !!data.data?.friendEnabled
            this.$message.success(enabled ? '朋友功能已开启' : '朋友功能已关闭')
          } else {
            this.friendEnabled = prev
            this.$message.error(data.message || '操作失败')
          }
        } catch (error) {
          this.friendEnabled = prev
          this.$message.error('操作失败')
        } finally {
          this.friendFeatureLoading = false
        }
      },
      handleCancel() {
        this.isEditing = false
        this.fetchUserProfile()
      },
      handleSave() {
        this.$refs.userForm.validate(async (valid) => {
          if (valid) {
            try {
              const token = localStorage.getItem('jwt_token');
              const birthday = this.userForm.birthday ? 
                parseInt(this.userForm.birthday.getFullYear() * 10000 + 
                (this.userForm.birthday.getMonth() + 1) * 100 + 
                this.userForm.birthday.getDate()) : null;

              const lat = this.userForm.lat ? Math.round(this.userForm.lat * 10000) : 0;
              const lng = this.userForm.lng ? Math.round(this.userForm.lng * 10000) : 0;
              const homeLat = this.userForm.homeLat ? Math.round(this.userForm.homeLat * 10000) : 0;
              const homeLng = this.userForm.homeLng ? Math.round(this.userForm.homeLng * 10000) : 0;

              const photos = (this.userForm.photos || [])
                .map(photo => photoUrlToStoragePath(photo))
                .filter(photo => photo && photo.trim() !== '')
              const mainPhoto = photos.length > 0 ? photos[0] : ''
              const avatar = photoUrlToStoragePath(this.userForm.avatar)

              const response = await fetch('/api/v1/user/profile/update', {
                method: 'POST',
                headers: {
                  'Authorization': `Bearer ${token}`,
                  'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                  nickname: this.userForm.nickname,
                  avatar: avatar,
                  sex: this.userForm.gender,
                  birthday: birthday,
                  height: this.userForm.height,
                  weight: this.userForm.weight,
                  country: this.userForm.country,
                  province: this.userForm.province,
                  city: this.userForm.city,
                  district: this.userForm.district,
                  pcaValue: this.userForm.pcaValue,
                  homeCountry: this.userForm.homeCountry,
                  homeProvince: this.userForm.homeProvince,
                  homeCity: this.userForm.homeCity,
                  homeDistrict: this.userForm.homeDistrict,
                  homePcaValue: this.userForm.homePcaValue,
                  location: this.userForm.location,
                  education: this.userForm.education,
                  school: this.userForm.school,
                  occupation: this.userForm.occupation,
                  workplace: this.userForm.workplace,
                  income: this.userForm.income,
                  maritalStatus: this.userForm.maritalStatus,
                  boyCount: this.userForm.boyCount,
                  girlCount: this.userForm.girlCount,
                  religion: this.userForm.religion,
                  hobbies: this.userForm.hobbies,
                  tags: this.userForm.tags,
                  introduction: this.userForm.introduction,
                  email: this.userForm.email,
                  lat: lat,
                  lng: lng,
                  homeLat: homeLat,
                  homeLng: homeLng,
                  photos: photos,
                  photo: mainPhoto
                })
              });

              const data = await response.json();
              if (data.code === 200) {
                this.isEditing = false;
                this.$message.success('保存成功');
                await this.fetchUserProfile();
              } else {
                this.$message.error(data.message || '保存失败');
              }
            } catch (error) {
              console.error('保存用户信息失败:', error);
              this.$message.error('保存失败');
            }
          }
        });
      },
      goChangePassword() {
        this.$router.push('/user/password')
      },
      handleAvatarSuccess(res) {
        if (res.code === 200) {
          this.userForm.avatar = photoPathToUrl(res.filename || res.message)
          this.$message.success('头像上传成功')
        } else {
          this.$message.error(res.message || '头像上传失败')
        }
      },
      beforeAvatarUpload(file) {
        return validatePhotoUpload(file, (msg) => this.$message.error(msg))
      },
      handlePhotoSuccess(res) {
        if (res.code === 200) {
          const path = res.filename || res.message
          const url = photoPathToUrl(path)
          const fileName = path.split('/').pop()
          
          const fileObj = {
            name: fileName,
            url: url,
            status: 'success',
            uid: Date.now()
          }
          
          this.userForm.photos.push(url)
          this.photoFileList.push(fileObj)
          
          this.$nextTick(() => {
            this.photoFileList = [...this.photoFileList]
          })
          
          this.$message.success('照片上传成功')
        } else {
          this.$message.error(res.message || '照片上传失败')
        }
      },
      handlePhotoRemove(file) {
        const index = this.photoFileList.findIndex(item => item.url === file.url)
        if (index !== -1) {
          this.userForm.photos.splice(index, 1)
          this.photoFileList.splice(index, 1)
        }
      },
      beforePhotoUpload(file) {
        return validatePhotoUpload(file, (msg) => this.$message.error(msg))
      },
      handleCitySelect(obj) {
        console.log('Selected city:', obj)
        
        const location = obj.location || ''
        const lng = location.lng ? location.lng / 10000 : null
        const lat = location.lat ? location.lat / 10000 : null
        console.log('Normalized coordinates:', { lng, lat })

        if (lng && lat) {
          const map = document.getElementById('current-location-map')
          if (map && map.__vue__) {
            map.__vue__.setCenter([lng, lat])
          }
        }

        const address = obj.address || ''
        
        const provinceMatch = address.match(/(.+?省)/)
        const cityMatch = address.match(/(.+?市)/)
        const districtMatch = address.match(/(.+?区|.+?县|.+?市)/)
        console.log(address)
        console.log(provinceMatch)
        console.log(cityMatch)
        console.log(districtMatch)
        var city = ''
        var district = ''
        var province = ''

        if (cityMatch) {
          city = cityMatch[1]
        }        
        if (districtMatch) {
          district = districtMatch[1]
        }
        if (provinceMatch) {
          province = provinceMatch[1]
        } 
        city = city.replace(province,"")
        district = district.replace(province,"").replace(city,"")
        
        if (province=="") {
          province = city.replace("市","")
        }

        this.userForm.city = city.replace('市', '')
        this.userForm.province = province.replace('省', '')
        this.userForm.district = district.replace(/[市区县]$/, '')
        this.userForm.country = '中国'
        this.userForm.location = address
        this.userForm.lng = lng
        this.userForm.lat = lat
      },

      handleHomeCitySelect(obj) {
        console.log('Selected home city:', obj)
        
        const location = obj.location || ''
        const lng = location.lng ? location.lng / 10000 : null
        const lat = location.lat ? location.lat / 10000 : null
        console.log('Normalized home coordinates:', { lng, lat })

        if (lng && lat) {
          const map = document.getElementById('home-location-map')
          if (map && map.__vue__) {
            map.__vue__.setCenter([lng, lat])
          }
        }

        const address = obj.address || ''
        
        const provinceMatch = address.match(/(.+?省)/)
        const cityMatch = address.match(/(.+?市)/)
        const districtMatch = address.match(/(.+?区|.+?县|.+?市)/)
        console.log(address)
        console.log(provinceMatch)
        console.log(cityMatch)
        console.log(districtMatch)
        var city = ''
        var district = ''
        var province = ''

        if (cityMatch) {
          city = cityMatch[1]
        }        
        if (districtMatch) {
          district = districtMatch[1]
        }
        if (provinceMatch) {
          province = provinceMatch[1]
        } 
        city = city.replace(province,"")
        district = district.replace(province,"").replace(city,"")
        
        if (province=="") {
          province = city.replace("市","")
        }

        this.userForm.homeCity = city.replace('市', '')
        this.userForm.homeProvince = province.replace('省', '')
        this.userForm.homeDistrict = district.replace(/[市区县]$/, '')
        this.userForm.homeCountry = '中国'
        this.userForm.homeLng = lng
        this.userForm.homeLat = lat
      },

      handleMapClick(event) {
        const lng = event.lngLat.lng
        const lat = event.lngLat.lat
        console.log('Map clicked coordinates:', { lng, lat })
        
        const normalizedLng = Math.round(lng * 10000)
        const normalizedLat = Math.round(lat * 10000)
        console.log('Normalized coordinates for saving:', { normalizedLng, normalizedLat })
        
        if (this.mapDialog.target === 'home' || event.target?.id === 'home-location-map') {
          this.userForm.homeLng = lng
          this.userForm.homeLat = lat
        } else {
          this.userForm.lng = lng
          this.userForm.lat = lat
        }
      },
      moveToFirst(file) {
        const index = this.photoFileList.findIndex(item => item.url === file.url)
        if (index !== -1) {
          const newFileList = [...this.photoFileList]
          const newPhotos = [...this.userForm.photos]
          
          const [removed] = newFileList.splice(index, 1)
          const [removedPhoto] = newPhotos.splice(index, 1)
          
          newFileList.unshift(removed)
          newPhotos.unshift(removedPhoto)
          
          this.photoFileList = newFileList
          this.userForm.photos = newPhotos
          
          this.$message.success('照片已移至首位')
        }
      },
    }
  }
  </script>
  
  <style scoped>
  .user-info-container {
    max-width: 1800px;
    margin: 0 auto;
    padding: 20px;
    box-sizing: border-box;
  }

  .user-info-card,
  .security-card {
    margin-bottom: 20px;
  }
  
  .card-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }
  
  .card-header h2 {
    margin: 0;
    font-size: 20px;
  }
  
  .edit-buttons {
    display: flex;
    gap: 10px;
  }
  
  .avatar-uploader {
    text-align: center;
  }
  
  .avatar-uploader .el-upload {
    border: 1px dashed var(--el-border-color);
    border-radius: 6px;
    cursor: pointer;
    position: relative;
    overflow: hidden;
    transition: var(--el-transition-duration-fast);
    z-index: 20;
  }
  
  .avatar-uploader .el-upload:hover {
    border-color: var(--el-color-primary);
  }
  
  .avatar-uploader-icon {
    font-size: 28px;
    color: #8c939d;
    width: 178px;
    height: 178px;
    text-align: center;
    line-height: 178px;
    z-index: 20;
  }
  
  .avatar {
    width: 178px;
    height: 178px;
    display: block;
    object-fit: cover;
  }

  .avatar-container {
    position: relative;
    width: 178px;
    height: 178px;
    display: inline-block;
  }

  .avatar-audit-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.6);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1;
    pointer-events: none;
  }

  .photo-audit-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.6);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1;
    pointer-events: none;
  }

  .audit-text {
    color: white;
    font-size: 14px;
    font-weight: bold;
    background-color: rgba(255, 0, 0, 0.8);
    padding: 4px 8px;
    border-radius: 4px;
  }

  .photo-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
    gap: 16px;
    margin-top: 16px;
  }

  .photo-item-container {
    position: relative;
    width: 100%;
    height: 100%;
  }

  .photo-actions {
    position: absolute;
    top: 8px;
    right: 8px;
    display: flex;
    gap: 8px;
    opacity: 0;
    transition: opacity 0.3s;
    z-index: 20;
  }

  .photo-item-container:hover .photo-actions {
    opacity: 1;
  }

  .photo-item {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .username-field {
    width: 100%;
  }

  .friend-feature-bar {
    margin-bottom: 20px;
    padding: 12px 16px;
    background: #f5f7fa;
    border-radius: 8px;
  }

  .friend-feature-row {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  .friend-feature-label {
    font-weight: 600;
    color: #303133;
  }

  .username-auto-hint {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;
    margin-bottom: 10px;
  }

  .username-current {
    font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
    font-size: 14px;
    color: #606266;
    word-break: break-all;
  }

  .username-display {
    font-size: 15px;
    color: #303133;
    word-break: break-all;
  }

  .field-hint {
    margin: 6px 0 10px;
    font-size: 12px;
    color: #909399;
    line-height: 1.45;
  }

  .profile-summary {
    display: flex;
    gap: 16px;
    align-items: center;
    padding: 14px 16px;
    margin: 8px 0 20px;
    background: #f7f8fa;
    border: 1px solid #ebeef5;
    border-radius: 10px;
  }
  .profile-summary__avatar {
    width: 72px;
    height: 72px;
    border-radius: 50%;
    object-fit: cover;
    background: #fff;
  }
  .profile-summary__name {
    font-size: 18px;
    font-weight: 600;
    color: #303133;
  }
  .profile-summary__meta {
    display: flex;
    flex-wrap: wrap;
    gap: 8px 14px;
    margin-top: 6px;
    font-size: 13px;
    color: #606266;
  }
  .profile-summary__tags {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    margin-top: 8px;
  }
  .profile-section-title {
    margin: 8px 0 12px;
    padding-top: 8px;
    font-size: 16px;
    font-weight: 600;
    color: #303133;
    border-top: 1px solid #ebeef5;
  }
  .profile-section-title:first-of-type {
    border-top: none;
    padding-top: 0;
  }
  .place-row {
    display: flex;
    gap: 8px;
    align-items: center;
  }
  .place-row .el-input { flex: 1; }
  .ai-btn { margin-top: 8px; }

  @media (max-width: 767px) {
    .user-info-container {
      padding: 0 !important;
    }
    .profile-summary {
      align-items: flex-start;
    }
    .place-row {
      flex-direction: column;
      align-items: stretch;
    }
  }
</style>